import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GetMasterDataDeductionSubTypesDTO } from "@src/domain/dto/masterData";
import type { MasterDataDeductionType } from "@src/domain/entities/masterData";
import { GetMasterDataDeductionSubTypesUseCase } from "@src/application/useCases/masterData";
import { MasterDataDeductionTypeRepository } from "@src/infrastructure/repositories/masterData";
import {
  getMasterDataListCacheEntry,
  invalidateMasterDataListCacheScopes,
  masterDataDeductionSubTypesCacheKey,
  setMasterDataListCacheEntry,
} from "@src/presentation/providers/masterData/masterDataListCache";

interface UseMasterDataDeductionSubTypesOptions {
  isOpen: boolean;
  parentTypeId: number | null;
  brandKey: string;
  refetchParent: () => void;
  pageSize: number;
}

export function useMasterDataDeductionSubTypes({
  isOpen,
  parentTypeId,
  brandKey,
  refetchParent,
  pageSize,
}: UseMasterDataDeductionSubTypesOptions) {
  const repository = useMemo(() => new MasterDataDeductionTypeRepository(), []);
  const getSubTypesUseCase = useMemo(
    () => new GetMasterDataDeductionSubTypesUseCase(repository),
    [repository],
  );

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [activeParentId, setActiveParentId] = useState(parentTypeId);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [entities, setEntities] = useState<MasterDataDeductionType[]>([]);

  if (parentTypeId !== activeParentId) {
    setActiveParentId(parentTypeId);
    setPage(1);
    setSearch("");
    setEntities([]);
    setTotal(0);
    setError(null);
  }

  const cacheKey =
    isOpen && parentTypeId != null
      ? masterDataDeductionSubTypesCacheKey(
          brandKey,
          parentTypeId,
          page,
          pageSize,
          search,
        )
      : null;
  const cached = cacheKey
    ? getMasterDataListCacheEntry<MasterDataDeductionType>(cacheKey)
    : undefined;
  const items = cached?.items ?? entities;
  const itemTotal = cached?.total ?? total;
  const loading =
    isOpen &&
    parentTypeId != null &&
    cached == null &&
    entities.length === 0 &&
    error == null;

  const refetchParentRef = useRef(refetchParent);
  refetchParentRef.current = refetchParent;

  const handleSaved = useCallback(() => {
    invalidateMasterDataListCacheScopes([
      "deduction-types",
      "deduction-types-lookup",
      "deduction-subtypes-modal",
    ]);
    setPage(1);
    setEntities([]);
    setTotal(0);
    setError(null);
    setReloadToken((token) => token + 1);
    refetchParentRef.current();
  }, []);

  const handleSetSearch = useCallback((next: string) => {
    setSearch(next);
    setPage(1);
  }, []);

  const resetOnClose = useCallback(() => {
    setReloadToken(0);
    setSearch("");
    setPage(1);
  }, []);

  useEffect(() => {
    if (!isOpen || parentTypeId == null || cacheKey == null) {
      return;
    }
    if (getMasterDataListCacheEntry(cacheKey)) {
      return;
    }

    let cancelled = false;

    void getSubTypesUseCase
      .execute(
        new GetMasterDataDeductionSubTypesDTO({
          parentId: parentTypeId,
          search: search.trim() || null,
          page_number: page,
          page_size: pageSize,
        }),
      )
      .then((result) => {
        if (cancelled) return;
        setMasterDataListCacheEntry(cacheKey, {
          items: result.items,
          total: result.total,
          page: result.page,
          limit: result.limit,
        });
        setEntities(result.items);
        setTotal(result.total);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEntities([]);
        setTotal(0);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load deduction sub-types",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    cacheKey,
    getSubTypesUseCase,
    isOpen,
    page,
    pageSize,
    parentTypeId,
    reloadToken,
    search,
  ]);

  return {
    page,
    setPage,
    pageSize,
    search,
    setSearch: handleSetSearch,
    items,
    itemTotal,
    loading,
    error,
    handleSaved,
    resetOnClose,
    repository,
  };
}
